import { useState, useRef, useEffect } from "react";
import { CURRENCY_MAP } from "../../constants";

export default function PublicTablesSection({ event }) {
  const [tables, setTables] = useState(event?.tables || []);
  const [selectedId, setSelectedId] = useState(null);
  const [imgNaturalSize, setImgNaturalSize] = useState({ w: 0, h: 0 });

  const mapRef = useRef(null);
  const selectedTable = tables.find((t) => t.id === selectedId);

  const formatCurrency = (amount) => {
    const symbol = CURRENCY_MAP[event.currency] || event.currency;
    return `${symbol} ${parseFloat(amount).toFixed(2)}`;
  };

  const handleImageLoad = (e) => {
    setImgNaturalSize({
      w: e.target.naturalWidth,
      h: e.target.naturalHeight,
    });
  };

  if (!event?.table_map) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-3 text-gray-500">
        <span className="text-3xl">🗺</span>
        <p className="text-sm">No hay mapa de mesas disponible</p>
      </div>
    );
  }

  return (
    <div className="mt-4 mb-6">
      {/* TITLE */}
      <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
        MESAS
      </p>
      <h2 className="text-2xl md:text-3xl font-semibold mt-1 mb-6">
        Selecciona tu Mesa
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — MAP */}
        <div className="bg-[#031c14] border border-green-900/30 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-green-900/20">
            <p className="text-xs text-gray-400">
              Toca una mesa para ver los detalles
            </p>
          </div>

          <div className="relative" ref={mapRef}>
            <img
              src={event.table_map}
              alt="Table map"
              className="w-full object-contain block"
              draggable={false}
              onLoad={handleImageLoad}
            />

            {/* HOTSPOTS */}
            {tables.map((t) => {
              const isSelected = t.id === selectedId;
              const xPct = imgNaturalSize.w
                ? (t.map_x / imgNaturalSize.w) * 100
                : 0;
              const yPct = imgNaturalSize.h
                ? (t.map_y / imgNaturalSize.h) * 100
                : 0;
              const size = t.map_size || 24;
              const isCircle = t.map_shape === "circle";

              // Color based on status

              const statusStyle = isSelected
                ? "bg-green-700 border-white text-black scale-125"
                : "bg-[#03211a] border-green-900/50 text-gray-400 hover:scale-110";

              return (
                <div
                  key={t.id}
                  style={{
                    position: "absolute",
                    left: `${xPct}%`,
                    top: `${yPct}%`,
                    width: `${size}px`,
                    height: `${size}px`,
                    transform: "translate(-50%, -50%)",
                    zIndex: isSelected ? 20 : 10,
                  }}
                >
                  <button
                    onClick={() =>
                      t.display_status !== "reserved" &&
                      setSelectedId(t.id === selectedId ? null : t.id)
                    }
                    style={{
                      width: "100%",
                      height: "100%",
                      fontSize: `${Math.max(8, size * 0.35)}px`,
                      borderRadius: isCircle ? "50%" : "4px",
                      cursor:
                        t.display_status === "reserved"
                          ? "not-allowed"
                          : "pointer",
                      userSelect: "none",
                    }}
                    className={`font-bold border-2 shadow-lg flex items-center justify-center w-full h-full transition-all ${statusStyle}`}
                    title={t.name}
                  >
                    {t.name}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — DETAIL PANEL */}
        <div className="bg-[#031c14] border border-green-900/30 rounded-xl flex flex-col min-h-[300px]">
          {selectedTable ? (
            <>
              {/* HEADER */}
              <div className="px-5 py-4 border-b border-green-900/20 flex items-center justify-between">
                <div>
                  <p className="text-[10px] tracking-widest text-green-400/70 uppercase mb-1">
                    Detalles de la mesa
                  </p>
                  <h3 className="text-lg font-semibold text-white">
                    Mesa {selectedTable.name}
                  </h3>
                </div>
                <div className="w-10 h-10 rounded-xl bg-green-900/30 border border-green-700/40 text-green-400 text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {selectedTable.name}
                </div>
              </div>

              {/* RIGHT — DETAIL PANEL */}
              {/* DETAILS */}
              <div className="flex-1 p-5 space-y-3 overflow-y-auto">
                {/* TOP 3 CARDS */}
                <div className="grid grid-cols-3 gap-3">
                  {/* PRICE */}
                  <div className="bg-[#021610] border border-green-900/30 rounded-xl p-3 text-center">
                    <p className="text-[10px] tracking-widest text-gray-500 uppercase mb-1">
                      Precio
                    </p>
                    <p className="text-green-400 font-bold text-sm">
                      {selectedTable.display_price != null
                        ? formatCurrency(selectedTable.display_price)
                        : "—"}
                    </p>
                  </div>

                  {/* CAPACITY */}
                  <div className="bg-[#021610] border border-green-900/30 rounded-xl p-3 text-center">
                    <p className="text-[10px] tracking-widest text-gray-500 uppercase mb-1">
                      Capacidad
                    </p>
                    <p className="text-white font-bold text-sm">
                      {selectedTable.capacity
                        ? `${selectedTable.capacity} pax`
                        : "—"}
                    </p>
                  </div>

                  {/* DRINKS CREDIT */}
                  <div className="bg-[#021610] border border-green-900/30 rounded-xl p-3 text-center">
                    <p className="text-[10px] tracking-widest text-gray-500 uppercase mb-1">
                      Crédito de bebidas
                    </p>
                    <p className="text-white font-bold text-sm">
                      {selectedTable.drinks_credit_enabled &&
                      selectedTable.drinks_credit_amount
                        ? formatCurrency(selectedTable.drinks_credit_amount)
                        : "—"}
                    </p>
                  </div>
                </div>

                {/* MERCHANDISE */}
                {selectedTable.merchandise_enabled &&
                  selectedTable.merchandise_description && (
                    <div className="bg-[#021610] border border-green-900/30 rounded-xl p-4">
                      <DetailRow
                        label="🎁 Merchandising incluido"
                        value={
                          <p className="text-gray-300 text-sm leading-relaxed">
                            {selectedTable.merchandise_description}
                          </p>
                        }
                        vertical
                      />
                    </div>
                  )}

                {/* DESCRIPTION */}
                {selectedTable.description && (
                  <div className="bg-[#021610] border border-green-900/30 rounded-xl p-4">
                    <DetailRow
                      label="Descripción"
                      value={
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {selectedTable.description}
                        </p>
                      }
                      vertical
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500 p-8 text-center">
              <p className="text-sm">Selecciona una mesa en el mapa</p>
              <p className="text-xs text-gray-600">
                Toca cualquier mesa disponible para ver sus detalles
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value, vertical = false }) {
  return (
    <div
      className={`${vertical ? "space-y-1.5" : "flex items-center justify-between gap-4"}`}
    >
      <p className="text-[11px] tracking-widest text-gray-400 uppercase flex-shrink-0">
        {label}
      </p>
      <div className={vertical ? "" : "text-right"}>{value}</div>
    </div>
  );
}
